import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Lock, Tag, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCart } from '@/context/CartContext';
import { useSeo } from '@/hooks/useSeo';
import { KlarnaBadge } from '@/components/KlarnaBadge';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function CheckoutPage() {
  useSeo({
    title: 'Checkout sicuro',
    description: 'Completa il tuo ordine in modo sicuro con Stripe. Pagamenti protetti e spedizione gratuita.',
    path: '/checkout',
    noindex: true,
  });

  const navigate = useNavigate();
  const { cart, cartId, loading } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: '',
    shipping_city: '',
    shipping_postal: '',
  });

  const formatPrice = (price) =>
    new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(price);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const discountAmount = appliedDiscount?.discount_amount || 0;
  const grandTotal = Math.max((cart?.total || 0) - discountAmount, 0);

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;
    setApplyingDiscount(true);
    try {
      const { data } = await axios.post(`${API}/discounts/validate`, {
        code: discountCode.trim().toUpperCase(),
        cart_id: cartId,
      });
      if (data.valid) {
        setAppliedDiscount(data);
        toast.success(`Codice ${data.code} applicato`);
      } else {
        setAppliedDiscount(null);
        toast.error(data.message || 'Codice non valido');
      }
    } catch (e) {
      setAppliedDiscount(null);
      toast.error('Codice non valido');
    } finally {
      setApplyingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cart?.items?.length) {
      toast.error('Il carrello è vuoto');
      return;
    }
    setSubmitting(true);
    try {
      const orderResponse = await axios.post(`${API}/orders`, {
        cart_id: cartId,
        ...formData,
        discount_code: appliedDiscount?.code || null,
      });
      const orderId = orderResponse.data.id;
      const checkoutResponse = await axios.post(`${API}/checkout/session`, {
        order_id: orderId,
        origin_url: window.location.origin,
      });
      window.location.href = checkoutResponse.data.url;
    } catch (e) {
      console.error('Checkout error:', e);
      toast.error('Errore durante il checkout');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-ivory pt-24 md:pt-28 min-h-screen" data-testid="checkout-loading">
        <div className="max-w-editorial mx-auto px-6 md:px-12 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-line w-48" />
            <div className="h-80 bg-line" />
          </div>
        </div>
      </div>
    );
  }

  const isEmpty = !cart?.items || cart.items.length === 0;
  if (isEmpty) {
    navigate('/carrello');
    return null;
  }

  return (
    <div className="bg-ivory pt-24 md:pt-28 min-h-screen" data-testid="checkout-page">
      <header className="px-6 md:px-12 lg:px-16 py-12 hairline-b">
        <div className="max-w-editorial mx-auto">
          <button
            onClick={() => navigate('/carrello')}
            className="inline-flex items-center gap-2 text-eyebrow text-muted hover:text-bordeaux transition-colors duration-hover mb-5 group"
            data-testid="back-to-cart"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform duration-hover" />
            Torna al Carrello
          </button>
          <h1 className="font-display text-h1 text-ink">Checkout</h1>
        </div>
      </header>

      <div className="max-w-editorial mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7 xl:col-span-8">
            <form onSubmit={handleSubmit} className="space-y-10">
              <section>
                <h2 className="font-display text-h3 text-ink mb-2">Informazioni di Contatto</h2>
                <p className="text-small text-muted mb-6">Ti contatteremo per confermare l'ordine.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <Label htmlFor="customer_name" className="text-eyebrow text-ink">Nome Completo *</Label>
                    <Input id="customer_name" name="customer_name" value={formData.customer_name}
                      onChange={handleChange} required className="mt-2" data-testid="input-name" />
                  </div>
                  <div>
                    <Label htmlFor="customer_email" className="text-eyebrow text-ink">Email *</Label>
                    <Input id="customer_email" name="customer_email" type="email"
                      value={formData.customer_email} onChange={handleChange} required
                      className="mt-2" data-testid="input-email" />
                  </div>
                  <div>
                    <Label htmlFor="customer_phone" className="text-eyebrow text-ink">Telefono *</Label>
                    <Input id="customer_phone" name="customer_phone" type="tel"
                      value={formData.customer_phone} onChange={handleChange} required
                      className="mt-2" data-testid="input-phone" />
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-display text-h3 text-ink mb-2">Indirizzo di Spedizione</h2>
                <p className="text-small text-muted mb-6">Consegna assicurata in 2-3 giorni lavorativi.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <Label htmlFor="shipping_address" className="text-eyebrow text-ink">Indirizzo *</Label>
                    <Input id="shipping_address" name="shipping_address"
                      value={formData.shipping_address} onChange={handleChange} required
                      className="mt-2" data-testid="input-address" />
                  </div>
                  <div>
                    <Label htmlFor="shipping_city" className="text-eyebrow text-ink">Città *</Label>
                    <Input id="shipping_city" name="shipping_city"
                      value={formData.shipping_city} onChange={handleChange} required
                      className="mt-2" data-testid="input-city" />
                  </div>
                  <div>
                    <Label htmlFor="shipping_postal" className="text-eyebrow text-ink">CAP *</Label>
                    <Input id="shipping_postal" name="shipping_postal"
                      value={formData.shipping_postal} onChange={handleChange} required
                      className="mt-2" data-testid="input-postal" />
                  </div>
                </div>
              </section>

              <Button
                type="submit"
                disabled={submitting}
                size="xl"
                variant="accent"
                className="w-full"
                data-testid="submit-checkout"
              >
                {submitting ? (
                  <span className="flex items-center gap-3">
                    <div className="w-4 h-4 border-2 border-ivory/30 border-t-ivory rounded-full animate-spin" />
                    Elaborazione...
                  </span>
                ) : (
                  <span className="flex items-center gap-3">
                    <CreditCard className="w-4 h-4" />
                    Procedi al Pagamento
                  </span>
                )}
              </Button>

              <div className="flex items-center justify-center gap-2 text-small text-muted">
                <Lock className="w-4 h-4" />
                Pagamento sicuro con Stripe
              </div>

              <KlarnaBadge amount={cart.total} className="justify-center" />
            </form>
          </div>

          <aside className="lg:col-span-5 xl:col-span-4">
            <div className="border border-line p-8 sticky top-28">
              <h2 className="font-display text-h3 text-ink mb-8">Riepilogo</h2>

              <div className="space-y-4 mb-6">
                {cart.items.map((item) => (
                  <div key={`${item.product_id}-${item.size || ''}`} className="flex gap-4">
                    <div className="w-16 h-20 bg-secondary rounded-xl flex-shrink-0 overflow-hidden">
                      <img src={item.product?.image_url} alt={item.product?.name}
                        className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-ink font-medium truncate">{item.product?.name}</p>
                      <p className="text-eyebrow text-muted mt-1">Qtà: {item.quantity}{item.size ? ` · Misura ${item.size}` : ''}</p>
                      <p className="text-sm text-ink mt-1 tabular-nums">{formatPrice(item.item_total)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <dl className="hairline-t pt-5 space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted">Subtotale</dt>
                  <dd className="text-ink font-medium tabular-nums">{formatPrice(cart.total)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted">Spedizione</dt>
                  <dd className="text-bordeaux font-medium">Gratuita</dd>
                </div>
                {appliedDiscount && (
                  <div className="flex justify-between" data-testid="checkout-discount-line">
                    <dt className="text-muted flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5" />Sconto ({appliedDiscount.code})
                    </dt>
                    <dd className="text-bordeaux font-medium tabular-nums">− {formatPrice(discountAmount)}</dd>
                  </div>
                )}
              </dl>

              {/* Codice sconto */}
              <div className="hairline-t mt-5 pt-5">
                {appliedDiscount ? (
                  <div className="flex items-center justify-between bg-bordeaux/5 border border-bordeaux/20 rounded-xl px-3 py-2.5" data-testid="applied-discount">
                    <span className="text-sm text-ink flex items-center gap-2">
                      <Tag className="w-4 h-4 text-bordeaux" />
                      <span className="font-medium">{appliedDiscount.code}</span> applicato
                    </span>
                    <button
                      type="button"
                      onClick={removeDiscount}
                      className="text-muted hover:text-ink transition-colors"
                      aria-label="Rimuovi codice"
                      data-testid="remove-discount-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      placeholder="Codice sconto"
                      className="uppercase"
                      data-testid="discount-code-field"
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyDiscount(); } }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={applyDiscount}
                      disabled={applyingDiscount || !discountCode.trim()}
                      data-testid="apply-discount-btn"
                    >
                      {applyingDiscount ? '…' : 'Applica'}
                    </Button>
                  </div>
                )}
              </div>

              <div className="hairline-t mt-5 pt-5">
                <div className="flex justify-between items-baseline">
                  <span className="text-eyebrow text-ink">Totale</span>
                  <span
                    className="font-display text-h3 text-ink tabular-nums"
                    data-testid="checkout-total"
                  >
                    {formatPrice(grandTotal)}
                  </span>
                </div>
                <KlarnaBadge amount={grandTotal} className="mt-3" />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
